/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { ColumnDef, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    Alert,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SearchInput,
    Table,
    Button,
    MoreButton,
    AlertDialog,
    AlertDialogContent,
    Card,
} from "@jhool-io/fe-components";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    formatDate,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    truncateString,
} from "../../../../utils/helpers";
import {
    INote,
    ISuperviseeNote,
    NoteLabels,
    NoteStatus,
    NoteTypes,
} from "../../../../modules/notes/types/notes.types";
import { useFetchSuperviseeNotesList } from "../../../../hooks/queries/note";
import { useDebounce } from "../../../../hooks/helpers";
import ListState from "../../../ListState/ListState";
import { CPT_CODES, LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import ApproveNote from "../../ApproveNote/ApproveNote";
import { UserPermisions, UserRole } from "../../../../utils/types/user";
import ArchiveNote from "../../NoteContent/ArchiveNote/ArchiveNote";
import {
    useChangeNoteArchiveStatus,
    useDeleteDraftNote,
} from "../../../../hooks/mutations/note";
import useToast from "../../../../hooks/useToast";
import {
    getCptCodeToDisplay,
    getNoteStatusTag,
    getNoteTypeTextWithTag,
    getSelectedAuthorName,
    updateAuthorFilterSearchParams,
} from "../../../../modules/notes/helpers/notes.helpers";
import CheckPrimaryColorIcon from "../../../Icons/CheckPrimaryColor";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import useShowInsuranceOverSelfPay from "../../../../modules/practice-settings/hooks/useShowInsuranceOverSelfPay";
import { useAppSelector } from "../../../../hooks/useRedux";
import Loader from "../../../../shared-ui/Loader/Loader";
import { useFetchProviders } from "../../../../modules/providers/hooks/providers.queries";
import { useDoesUserHaveRole } from "../../../../hooks/permissions";
import { useCanCreateNote } from "../../../../modules/notes/hooks/useCanCreateNote";

type Dialogs = "approve" | "delete-confirmation" | "archive";

export default function SuperviseeNoteList({
    setNotesCount,
}: {
    setNotesCount?: (count: string) => void;
}) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [showAuthorPopover, setShowAuthorPopover] = React.useState(false);
    const [authorSearchString, setAuthorSearchString] = React.useState("");

    // Get filters from url
    const providerNameFilter = searchParams.get("provider_name") || "";
    const authorFilter = searchParams.get("author") || "";
    const authorNameFilter = searchParams.get("author_name") || "";
    const cptCodeFilter = searchParams.get("cpt_code") || "";
    const statusFilter = searchParams.getAll("status[]");
    const noteTypeFilter = searchParams.getAll("type[]");
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const showArchivedFilter = searchParams.get("show_archived") || "false";
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const debouncedProviderNameFilter = useDebounce(providerNameFilter);
    const debouncedAuthorSearchString = useDebounce(authorSearchString);

    const [rowSelection, setRowSelection] = React.useState({});
    const [selectedNoteId, setSelectedNoteId] = React.useState<string | null>(
        null
    );
    const [selectedNotes, setSelectedNotes] = React.useState<string[]>([]);
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [getSelectedData, setGetSelectedData] = React.useState<any>();

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const { toast } = useToast();

    const showInsOverSelfPay = useShowInsuranceOverSelfPay();

    const handleDialogInView = (modal: Dialogs | null) => {
        setDialogInView(() => modal);
    };

    const navigate = useNavigate();

    const { practice } = useAppSelector((state) => state.userPractice);

    const handleArchivedFilter = () => {
        if (showArchivedFilter === "all") {
            return "";
        }
        return showArchivedFilter;
    };

    const { data, isLoading, error, isSuccess } = useFetchSuperviseeNotesList(
        practice?.provider_id || "",
        {
            status: statusFilter,
            cpt_code: cptCodeFilter,
            type: noteTypeFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            provider_name: debouncedProviderNameFilter,
            page: pageFilter,
            limit: limitFilter,
            show_archived: handleArchivedFilter(),
            author_id: authorFilter,
        },
        Boolean(
            !(
                statusFilter &&
                statusFilter.length === 1 &&
                /,+/.test(statusFilter[0])
            )
        )
    );

    const providers = useFetchProviders({
        search_string: debouncedAuthorSearchString,
        page: 1,
        limit: 20,
    });

    const getAuthorTextToDisplay = getSelectedAuthorName(
        providers.data?.data,
        data?.data,
        authorFilter,
        authorNameFilter
    );

    const handleSetProviderNameFilter = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (e.target.value === "") searchParams.delete("provider_name");
        else searchParams.set("provider_name", e.target.value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetAuthorFilter = (author: string, authorName = "") => {
        setSearchParams(
            updateAuthorFilterSearchParams(searchParams, author, authorName)
        );
    };

    const handleSetTypeFilter = (type: string) => {
        if (noteTypeFilter.includes(type)) {
            searchParams.delete("type[]", type);
        } else {
            searchParams.append("type[]", type);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetStatusFilter = (status: string) => {
        if (statusFilter.includes(status)) {
            searchParams.delete("status[]", status);
        } else {
            searchParams.append("status[]", status);
        }
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetCptCodeFilter = (cptCode: string) => {
        if (cptCode === "") searchParams.delete("cpt_code");
        else searchParams.set("cpt_code", cptCode);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetArchivedNotesFilter = (archived: string) => {
        searchParams.set("show_archived", archived);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleSetPageFilter = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleSetLimitFilter = (limit: number) => {
        searchParams.set("limit", String(limit));
        setSearchParams(searchParams);
    };

    // Clicked note navigation for web
    const handleRowClick = (row: Row<ISuperviseeNote>) => {
        if (row.original.status === NoteStatus.DRAFT) {
            navigate(
                `/notes/${row.original.note_id}/${row.original.client.client_id}/edit`
            );
        } else {
            navigate(
                `/notes/${row.original.client.client_id}/${row.original.note_id}?viewFrom=session_note`
            );
        }
    };

    const selectedIds = getSelectedData?.map(
        (item: Row<ISuperviseeNote>) => item.original.note_id
    );

    // Array of details of the selected notes(web)
    const notesDetails =
        selectedIds &&
        selectedIds.map((noteId: string) =>
            data?.data.find((note) => note.note_id === noteId)
        );

    // Array of details of the selected notes(mobile)
    const selectedNotesDetails =
        selectedNotes &&
        selectedNotes.map((noteId: string) =>
            data?.data.find((note) => note.note_id === noteId)
        );

    const detailsArray =
        selectedNotes.length > 0 ? selectedNotesDetails : notesDetails;

    const showButtons =
        getTotalAmountOfTableRowsSelected(rowSelection).amount > 0 ||
        selectedNotes.length > 0;

    // Handle approve action clicked
    const handleApproveActionClicked = (note: ISuperviseeNote) => {
        if (note.note_type === NoteTypes.SAFETY_PLAN) {
            toast({
                mode: "warning",
                message:
                    "Before giving your approval, please thoroughly review this safety plan note by opening it and going through its contents.",
            });
        } else {
            handleDialogInView("approve");
            setSelectedNoteId(note.note_id);
        }
    };

    const canArchiveNote = [
        UserPermisions.CLIENT_NOTES_ALL_READ,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
    ].some((permission) => practice?.permissions?.includes(permission));

    const canArchiveCodedNote = [
        UserRole.SUPER_ADMIN,
        UserRole.CODER,
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ].some((role) => practice?.roles?.includes(role));

    const showArchiveOption = (row: Row<ISuperviseeNote>) =>
        (canArchiveNote &&
            (row.original.status !== NoteStatus.CODED ||
                !row.original.date_of_service)) ||
        (canArchiveCodedNote && row.original.status === NoteStatus.CODED);

    const selectedNotesHaveClaim = detailsArray?.some((note: ISuperviseeNote) =>
        Boolean(note?.claim_id)
    );

    const isBillerOrBillerAdmin = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ]);
    const canCreateNote = useCanCreateNote();
    const canArchiveNoteWithClaim = (note: ISuperviseeNote) =>
        !note.claim_id || isBillerOrBillerAdmin;

    const canArchiveSelectedNotesWithClaim = () =>
        !selectedNotesHaveClaim || isBillerOrBillerAdmin;

    const showBulkArchiveBtn = () =>
        (canArchiveNote &&
            detailsArray?.every(
                (note: ISuperviseeNote) =>
                    note?.status !== NoteStatus.CODED || !note?.date_of_service
            )) ||
        (canArchiveCodedNote &&
            detailsArray?.every(
                (note: ISuperviseeNote) => note?.status === NoteStatus.CODED
            ));

    const unarchiveNote = useChangeNoteArchiveStatus();

    const deleteNote = useDeleteDraftNote();

    const queryClient = useQueryClient();

    const handleUnarchive = (id?: string) => {
        const dataToSend = {
            note_ids_to_archive: selectedIds.length === 0 ? [id] : selectedIds,
            archive_status: false,
        };

        unarchiveNote.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["supervisee"],
                });

                toast({
                    mode: "success",
                    message: res.message || "Note unarchived successfully",
                });

                navigate(`/notes`);

                setRowSelection({});
                setSelectedNotes([]);
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not unarchive note at this time",
                });
            },
        });
    };

    // Function to call for deleting note
    const handleDeleteNote = () => {
        const noteIds = selectedNotes.length > 0 ? selectedNotes : selectedIds;

        deleteNote.mutate(
            noteIds.length === 0 ? [selectedNoteId as string] : noteIds,
            {
                async onSuccess(res) {
                    await queryClient.invalidateQueries({
                        queryKey: [`notes`],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["supervisee"],
                    });
                    toast({
                        mode: "success",
                        message:
                            res.message || "Draft note(s) deleted successfully",
                    });
                    setSelectedNoteId(null);

                    setRowSelection({});
                    setSelectedNotes([]);
                    handleDialogInView(null);
                },
                onError(err) {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Cannot delete note(s) at this moment, please try again",
                    });
                },
            }
        );
    };

    // Check if note mutation actions are in progress
    const archiveNoteIsMutating = useIsMutating(["archive-note"]);
    const approveNoteIsMutating = useIsMutating(["approve-note"]);

    // Check if selected notes(mobile) contain notes with note type of safety_plan
    const notesToApproveHaveSafetyPlanMobile = data?.data.filter(
        (note) =>
            selectedNotes?.includes(note.note_id) &&
            note.note_type === NoteTypes.SAFETY_PLAN
    );

    // Check if selected notes(web) contain notes with note type of safety_plan
    const notesToApproveHaveSafetyPlanWeb = data?.data.filter(
        (note) =>
            selectedIds?.includes(note.note_id) &&
            note.note_type === NoteTypes.SAFETY_PLAN
    );

    // Notes ids to be apporoved(web)
    const noteIdsToApproveForWeb = () => {
        const arrayOfIdsOfSelectedSafetyPlanNotes =
            notesToApproveHaveSafetyPlanWeb?.map((note) => note.note_id);

        return selectedIds?.filter(
            (id: string) => !arrayOfIdsOfSelectedSafetyPlanNotes?.includes(id)
        );
    };

    // Notes ids to be apporoved(mobile)
    const noteIdsToApproveForMobile = () => {
        const arrayOfIdsOfSelectedSafetyPlanNotes =
            notesToApproveHaveSafetyPlanMobile?.map((note) => note.note_id);

        return selectedNotes?.filter(
            (id: string) => !arrayOfIdsOfSelectedSafetyPlanNotes?.includes(id)
        );
    };

    // Check to determine whether to show warning alert in the approve note modal
    const doesSelectedNotesHaveSafetyPlan =
        (notesToApproveHaveSafetyPlanMobile &&
            notesToApproveHaveSafetyPlanMobile.length > 0) ||
        (notesToApproveHaveSafetyPlanWeb &&
            notesToApproveHaveSafetyPlanWeb.length > 0);

    const handleApproveActionClick = () => {
        if (
            (noteIdsToApproveForWeb()?.length === 0 &&
                notesToApproveHaveSafetyPlanWeb &&
                notesToApproveHaveSafetyPlanWeb?.length > 0) ||
            (noteIdsToApproveForMobile()?.length === 0 &&
                notesToApproveHaveSafetyPlanMobile &&
                notesToApproveHaveSafetyPlanMobile?.length > 0)
        ) {
            toast({
                mode: "warning",
                message:
                    "You've only selected safety plan note(s), please thoroughly review each by opening and going through its contents before giving your approval.",
            });
        } else {
            handleDialogInView("approve");
        }
    };

    const getArchivedNoteFilterToDisplay = () => {
        if (showArchivedFilter === "true") {
            return "Archived notes";
        }
        return "Unarchived notes";
    };

    const handleNoteHasMoreActions = (row: Row<ISuperviseeNote>) => {
        if (
            row.original.status === NoteStatus.DRAFT ||
            row.original.status === NoteStatus.PENDING_SUPERVISOR_REVIEW ||
            showArchiveOption(row)
        ) {
            return true;
        }
        return false;
    };

    const columns: ColumnDef<ISuperviseeNote>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    handleChange={table.getToggleAllRowsSelectedHandler()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={row.getToggleSelectedHandler()}
                    indeterminate={row.getIsSomeSelected()}
                    name="row"
                    value="row"
                    isChecked={row.getIsSelected()}
                />
            ),
        },
        {
            accessorKey: "date_of_service",
            header: "DATE OF SERVICE",
            cell: ({ row }) =>
                row.original.date_of_service
                    ? formatDate(row.original.date_of_service)
                    : "--",
        },
        {
            accessorKey: "note_type",
            header: "NOTE TYPE",
            cell: ({ row }) => getNoteTypeTextWithTag(row.original.note_type),
        },
        {
            accessorKey: "provider",
            header: "SUPERVISEE NAME",
            cell: ({ row }) => (
                <span className="capitalize">
                    {displayNameInRightFormat({
                        firstName: row.original.provider?.first_name,
                        lastName: row.original.provider?.last_name,
                    })}
                </span>
            ),
        },
        {
            accessorKey: "client",
            header: "Client",
            cell: ({ row }) => (
                <span className="capitalize">
                    {displayNameInRightFormat({
                        firstName: row.original.client?.first_name,
                        lastName: row.original.client?.last_name,
                    })}
                </span>
            ),
        },
        {
            accessorKey: "cpt_code",
            header: "CPT CODE(S)",
            cell: ({ row }) =>
                getCptCodeToDisplay({
                    noteType: row.original.note_type,
                    cptCode: row.original.cpt_code,
                    additionalCptCodes: row.original.additional_cpt_codes,
                    unitCalculation:
                        row.original?.note_metadata?.unit_calculation,
                }),
        },
        {
            accessorKey: "external_reference_id",
            header: "claim ref no",
            cell: ({ row }) => row.original.external_reference_id ?? "--",
        },
        {
            accessorKey: "author",
            header: "CREATED BY",
            cell: ({ row }) =>
                row.original.author ? (
                    <span className="capitalize">
                        {displayNameInRightFormat({
                            firstName: row.original.author.first_name,
                            lastName: row.original.author.last_name,
                        })}
                    </span>
                ) : (
                    "--"
                ),
        },
        {
            accessorKey: "created_at",
            header: "CREATED ON",
            cell: ({ row }) => formatDate(row.original.created_at),
        },
        {
            accessorKey: "status",
            header: "STATUS",
            cell: ({ row }) =>
                getNoteStatusTag(row.original.status as NoteStatus),
        },
        {
            accessorKey: "claim_id",
            header: "INSURANCE PROVIDER",
            cell: ({ row }) => (
                <span
                    className={
                        row.original.client.self_pay && !showInsOverSelfPay
                            ? "normal-case"
                            : "uppercase"
                    }
                >
                    {row.original.client.self_pay && !showInsOverSelfPay
                        ? "Self pay"
                        : (row.original.client.insurance_provider_name &&
                              removeEnumUnderscore(
                                  row.original.client.insurance_provider_name ||
                                      ""
                              )) ||
                          "--"}
                </span>
            ),
        },
        {
            accessorKey: "note_id",
            header: "",
            cell: ({ row }) =>
                handleNoteHasMoreActions(row) && (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <MoreButton />
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {row.original.status ===
                                NoteStatus.PENDING_SUPERVISOR_REVIEW &&
                                !row.original.is_archived && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleApproveActionClicked(
                                                row.original
                                            );
                                        }}
                                    >
                                        Approve and sign note
                                    </DropdownItem>
                                )}
                            {showArchiveOption(row) &&
                                canArchiveNoteWithClaim(row.original) &&
                                row.original.is_archived === false &&
                                row.original.status !== NoteStatus.DRAFT && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDialogInView("archive");
                                            setSelectedNoteId(
                                                row.original.note_id
                                            );
                                        }}
                                    >
                                        Archive note
                                    </DropdownItem>
                                )}
                            {showArchiveOption(row) &&
                                row.original.is_archived && (
                                    <DropdownItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleUnarchive(
                                                row.original.note_id
                                            );
                                        }}
                                    >
                                        Unarchive note
                                    </DropdownItem>
                                )}
                            {row.original.status === NoteStatus.DRAFT && (
                                <DropdownItem
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDialogInView(
                                            "delete-confirmation"
                                        );
                                        setSelectedNoteId(row.original.note_id);
                                    }}
                                >
                                    Delete draft
                                </DropdownItem>
                            )}
                        </DropdownContent>
                    </Dropdown>
                ),
        },
    ];

    React.useEffect(() => {
        if (setNotesCount) {
            if (error) setNotesCount("");
            else if (isLoading) setNotesCount("");
            else if (data && data?.total_count > 0) {
                setNotesCount(String(data.total_count));
            } else if (data && data.data.length === 0) {
                setNotesCount("");
            }
        }
    }, [data, error, isLoading, setNotesCount]);

    React.useEffect(() => {
        if (
            statusFilter &&
            statusFilter.length === 1 &&
            /,+/.test(statusFilter[0])
        ) {
            searchParams.delete("status[]");
            statusFilter[0].split(",").forEach((status) => {
                searchParams.append("status[]", status);
            });

            setSearchParams(searchParams);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <Dialog open={dialogInView === "approve"}>
                <DialogContent
                    title="Approve and sign note"
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    saveText="Approve and sign note"
                    showFooter
                    submitBtnFormValue="note-approval"
                    isSubmitBtnDisabled={approveNoteIsMutating > 0}
                    isCancelBtnDisabled={approveNoteIsMutating > 0}
                >
                    <>
                        {doesSelectedNotesHaveSafetyPlan && (
                            <div className="mb-[--spacing-header]">
                                <Alert
                                    type="warning"
                                    description="Please note that selected safety plan notes will be excluded from this action. Kindly thoroughly review each safety plan note by opening it and going through its contents before approving and signing them individually."
                                />
                            </div>
                        )}
                        <ApproveNote
                            noteId={selectedNoteId as string}
                            selectedIds={
                                noteIdsToApproveForWeb() ||
                                noteIdsToApproveForMobile()
                            }
                            showDetails={false}
                            onFormSubmit={() => {
                                handleDialogInView(null);
                                setRowSelection({});
                                setSelectedNotes([]);
                            }}
                        />
                    </>
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "archive"}>
                <DialogContent
                    title="Archive Note"
                    variant="center"
                    showFooter
                    saveText="Archive Note"
                    submitBtnFormValue="archive-note"
                    handleCloseDialog={() => handleDialogInView(null)}
                    isSubmitBtnDisabled={archiveNoteIsMutating > 0}
                    isCancelBtnDisabled={archiveNoteIsMutating > 0}
                >
                    <ArchiveNote
                        onFormSubmit={() => {
                            handleDialogInView(null);
                            setRowSelection({});
                            setSelectedNotes([]);
                        }}
                        selectedIds={selectedIds}
                        noteId={selectedNoteId || ""}
                    />
                </DialogContent>
            </Dialog>
            <AlertDialog open={dialogInView === "delete-confirmation"}>
                <AlertDialogContent
                    title="Delete draft note?"
                    description="Are you sure you want to delete this draft note?"
                    isDeleting
                    saveText="Delete Draft"
                    onCancelClick={() => handleDialogInView(null)}
                    onSaveClick={handleDeleteNote}
                    isSaveBtnDisabled={deleteNote.isLoading}
                    isCancelBtnDisabled={deleteNote.isLoading}
                />
            </AlertDialog>

            <div>
                <Card classNames="mb-[--spacing-header]">
                    <div className="flex flex-col gap-[--spacing-items]">
                        <SearchInput
                            defaultValue={providerNameFilter}
                            onChange={handleSetProviderNameFilter}
                            containerClass="w-[400px] max-w-full"
                            placeholder="Search by supervisee"
                        />
                        <div className="flex items-center justify-between flex-wrap gap-[--spacing-items]">
                            <div className="flex items-center gap-[--spacing-items] flex-wrap">
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Note type</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent align="end" width="auto">
                                        {Object.values(NoteTypes).map(
                                            (type) => (
                                                <DropdownItem
                                                    key={type}
                                                    onClick={() =>
                                                        handleSetTypeFilter(
                                                            type
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={type}
                                                    values={noteTypeFilter}
                                                >
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            NoteLabels[type]
                                                        )
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton>Status</FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent align="end" width="auto">
                                        {Object.values(NoteStatus).map(
                                            (status) => (
                                                <DropdownItem
                                                    key={status}
                                                    onClick={() =>
                                                        handleSetStatusFilter(
                                                            status
                                                        )
                                                    }
                                                    isMulti
                                                    itemValue={status}
                                                    values={statusFilter}
                                                >
                                                    {makeStringFirstLetterCapital(
                                                        removeEnumUnderscore(
                                                            status
                                                        )
                                                    )}
                                                </DropdownItem>
                                            )
                                        )}
                                    </DropdownContent>
                                </Dropdown>
                                {(practice?.permissions?.includes(
                                    UserPermisions.AUDIT_DASHBOARD_READ
                                ) ||
                                    !practice?.provider_id) && (
                                    <Popover
                                        open={showAuthorPopover}
                                        onOpenChange={(open) => {
                                            setShowAuthorPopover(open);
                                            setAuthorSearchString("");
                                        }}
                                    >
                                        <PopoverTrigger
                                            asChild
                                            onClick={() =>
                                                setShowAuthorPopover(true)
                                            }
                                        >
                                            <FilterButton
                                                className={
                                                    authorFilter
                                                        ? "capitalize"
                                                        : "normal-case"
                                                }
                                            >
                                                {getAuthorTextToDisplay
                                                    ? truncateString(
                                                          getAuthorTextToDisplay
                                                      )
                                                    : "Author"}
                                            </FilterButton>
                                        </PopoverTrigger>
                                        <PopoverContent align="start">
                                            <SearchInput
                                                placeholder="Search by name"
                                                onChange={(e) =>
                                                    setAuthorSearchString(
                                                        e.target.value
                                                    )
                                                }
                                            />

                                            <div className="flex flex-col items-start max-h-[230px] overflow-y-auto">
                                                {providers.isLoading ? (
                                                    <Loader wrapperClassName="h-[150px]" />
                                                ) : null}
                                                {providers.error && (
                                                    <ListState
                                                        isError
                                                        context="general"
                                                        stateHelperText="Please try again later"
                                                        errorMsg={
                                                            providers.error
                                                                ?.response?.data
                                                                ?.message ||
                                                            "Error loading authors"
                                                        }
                                                        cardClass="h-[150px]"
                                                    />
                                                )}
                                                {providers.data?.data &&
                                                    providers.isSuccess && (
                                                        <>
                                                            <Button
                                                                size="auto"
                                                                variant="can-hover"
                                                                className="font-medium capitalize w-full justify-start my-1.5 shrink-0"
                                                                onClick={() => {
                                                                    handleSetAuthorFilter(
                                                                        ""
                                                                    );
                                                                    setShowAuthorPopover(
                                                                        false
                                                                    );
                                                                    setAuthorSearchString(
                                                                        ""
                                                                    );
                                                                }}
                                                            >
                                                                All
                                                            </Button>
                                                            {providers?.data?.data.map(
                                                                (provider) => (
                                                                    <Button
                                                                        key={
                                                                            provider.provider_id
                                                                        }
                                                                        size="auto"
                                                                        variant="can-hover"
                                                                        className="font-medium capitalize w-full justify-start mb-1.5 shrink-0"
                                                                        onClick={() => {
                                                                            handleSetAuthorFilter(
                                                                                provider.user_id,
                                                                                provider.provider_name
                                                                            );
                                                                            setShowAuthorPopover(
                                                                                false
                                                                            );
                                                                            setAuthorSearchString(
                                                                                ""
                                                                            );
                                                                        }}
                                                                    >
                                                                        {displayNameInRightFormat(
                                                                            provider.provider_name
                                                                        )}
                                                                        {authorFilter ===
                                                                        provider.user_id ? (
                                                                            <CheckPrimaryColorIcon />
                                                                        ) : null}
                                                                    </Button>
                                                                )
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                )}
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {cptCodeFilter || "CPT code"}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        <DropdownItem
                                            isSelected={!cptCodeFilter}
                                            onClick={() =>
                                                handleSetCptCodeFilter("")
                                            }
                                        >
                                            All
                                        </DropdownItem>
                                        {CPT_CODES.map((code) => (
                                            <DropdownItem
                                                key={code}
                                                isSelected={
                                                    cptCodeFilter === code
                                                }
                                                onClick={() =>
                                                    handleSetCptCodeFilter(code)
                                                }
                                            >
                                                {removeEnumUnderscore(code)}
                                            </DropdownItem>
                                        ))}
                                    </DropdownContent>
                                </Dropdown>
                                <Dropdown>
                                    <DropdownTrigger asChild>
                                        <FilterButton className="normal-case">
                                            {getArchivedNoteFilterToDisplay()}
                                        </FilterButton>
                                    </DropdownTrigger>
                                    <DropdownContent>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "true"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "true"
                                                )
                                            }
                                        >
                                            Archived notes
                                        </DropdownItem>
                                        <DropdownItem
                                            isSelected={
                                                showArchivedFilter === "false"
                                            }
                                            onClick={() =>
                                                handleSetArchivedNotesFilter(
                                                    "false"
                                                )
                                            }
                                        >
                                            Unarchived notes
                                        </DropdownItem>
                                    </DropdownContent>
                                </Dropdown>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <FilterButton>Date</FilterButton>
                                    </PopoverTrigger>
                                    <PopoverContent align="end">
                                        <div className="mb-1">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={fromDateFilterToDate}
                                                onChange={
                                                    handleFromDateFilterChange
                                                }
                                                maxDate={
                                                    toDateFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />
                                        </div>
                                        <DatePicker
                                            label="To"
                                            placeholderText="MM/DD/YYYY"
                                            selected={toDateFilterToDate}
                                            onChange={handleToDateFilterChange}
                                            minDate={fromDateFilterToDate}
                                            isClearable
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="w-full min-[500px]:w-auto">
                                {canCreateNote ? (
                                    <Button
                                        className="w-full cursor-pointer"
                                        onClick={() => navigate("/add-note")}
                                    >
                                        Create note
                                    </Button>
                                ) : null}
                            </div>
                        </div>
                    </div>
                    <TableFiltersDisplay
                        appliedFilters={[
                            {
                                key: "status[]",
                                values: statusFilter,
                            },
                            {
                                key: "type[]",
                                values: noteTypeFilter.map(
                                    (type) => NoteLabels[type]
                                ),
                            },
                        ]}
                        wrapperClass="mt-[--spacing-items]"
                    />
                </Card>
                {isLoading ? <Loader /> : null}
                {error && error?.response?.status !== 404 && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            error?.response?.data.message ||
                            `Cannot display supervisee notes list at this time please try again later`
                        }
                    />
                )}
                {data && isSuccess && data.data.length === 0 && (
                    <ListState
                        stateHelperText="Supervisee notes list will appear here once they are added"
                        isError={false}
                        emptyMessage="No note added yet"
                    />
                )}
                {isSuccess && data && data.data.length > 0 && (
                    <>
                        {showButtons && (
                            <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap">
                                <div className="font-medium text-base">
                                    {selectedNotes.length > 0
                                        ? `${selectedNotes.length} selected`
                                        : getTotalAmountOfTableRowsSelected(
                                              rowSelection
                                          ).amountString}
                                </div>
                                {detailsArray?.every(
                                    (note: ISuperviseeNote) =>
                                        note?.status ===
                                        NoteStatus.PENDING_SUPERVISOR_REVIEW
                                ) &&
                                    detailsArray.every(
                                        (note: ISuperviseeNote) =>
                                            note?.is_archived === false
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={handleApproveActionClick}
                                        >
                                            Approve and sign
                                        </Button>
                                    )}

                                {showBulkArchiveBtn() &&
                                    detailsArray.every(
                                        (note: ISuperviseeNote) =>
                                            note?.is_archived === false
                                    ) &&
                                    canArchiveSelectedNotesWithClaim() &&
                                    detailsArray?.every(
                                        (note: INote) =>
                                            note?.status !== NoteStatus.DRAFT
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleDialogInView("archive")
                                            }
                                        >
                                            Archive note
                                        </Button>
                                    )}

                                {showBulkArchiveBtn() &&
                                    detailsArray.every(
                                        (note: ISuperviseeNote) =>
                                            note?.is_archived
                                    ) && (
                                        <Button
                                            variant="secondary"
                                            onClick={() => handleUnarchive()}
                                        >
                                            Unarchive note
                                        </Button>
                                    )}

                                {detailsArray?.every(
                                    (note: ISuperviseeNote) =>
                                        note?.status === NoteStatus.DRAFT
                                ) && (
                                    <Button
                                        className="bg-danger text-white hover:bg-danger"
                                        onClick={() =>
                                            handleDialogInView(
                                                "delete-confirmation"
                                            )
                                        }
                                    >
                                        Delete draft
                                    </Button>
                                )}
                            </div>
                        )}
                        <div>
                            <Table
                                columns={columns}
                                data={data.data}
                                rowSelection={rowSelection}
                                setRowSelection={setRowSelection}
                                handleRowClick={handleRowClick}
                                setGetSelectedData={setGetSelectedData}
                                hasPagination={data.total_count > 20}
                                pagination={
                                    <Pagination
                                        totalCount={data.total_count}
                                        totalPages={data.total_pages}
                                        currentPage={pageFilter}
                                        onPageChange={handleSetPageFilter}
                                        limit={limitFilter}
                                        onLimitChange={handleSetLimitFilter}
                                    />
                                }
                            />
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
